<template>
  <div>
    <aha-modal :title="modalTitle" @hide="$emit('hide')">
      <div v-if="!hasTeam && !invitingMembers">
        <p class="mb-4">
          {{ $i18nPresenter.t('Unleash the power of teamwork on AhaSlides!') }} {{ team.name }}
        </p>
        <video
          width="100%"
          height="300px"
          id="invite-to-team"
          autoplay
          loop="true"
          muted
          class="mb-4"
        >
          <source :src="require('@/assets/animations/Invite2edit-CreateTeam2.mp4')" type="video/webm">
          {{ $i18nPresenter.t('Your browser does not support the video tag.') }}
        </video>
        <p class="font-bold mb-1">
          {{ $i18nPresenter.t('Team name:') }}
        </p>
        <div class="mb-4">
          <aha-input
            :placeholder="$i18nPresenter.t('Enter team name')"
            v-model="teamName"
            :max-length="100"
            focus
            @input="onInputTeamName"
          />
          <p v-if="teamNameError" class="text-red-50">
            {{ teamNameError }}
          </p>
        </div>
      </div>

      <div v-if="hasTeam && !creatingTeam" class="mb-4">
        <p class="font-bold mb-1">
          {{ $i18nPresenter.t('To team:') }}
        </p>
        <p>{{ team.name }}</p>
      </div>

      <p class="font-bold mb-1">
        {{ $i18nPresenter.t('Invitees:') }}
      </p>
      <p>{{ memberListText }}</p>

      <template #action>
        <aha-checkbox class="mr-auto" :value="dontShowAgain" @change="onToggleShowAgain">
          {{ $i18nPresenter.t("Don't ask again") }}
        </aha-checkbox>

        <aha-antd-button
          v-if="hasTeam"
          v-aha-emit-action
          name="invite-people-button"
          @click="invitePeople"
          :submitting="invitingMembers"
        >
          {{ $i18nPresenter.t('Invite') }}
        </aha-antd-button>
        <aha-antd-button
          v-else
          v-aha-emit-action
          name="create-team-and-invite-people-button"
          @click="createTeamAndInvitePeople"
          :submitting="creatingTeam"
        >
          {{ $i18nPresenter.t('Create') }}
        </aha-antd-button>
      </template>
    </aha-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import AccountApi from '@/api/account'
import InvitationApi from '@/api/invitation'
import eventCategories from '@/constant/eventCategories'
import EmailUtils from '@/utils/email'
import util from '@/util'

export default {
  name: 'InviteToTeamModal',
  props: {
    presentation: {
      type: Object,
      default: null,
    },
    user: {
      type: Object,
      default: null,
    },
    team: {
      type: Object,
      default: null,
    },
    toBeInvitedMembers: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      teamName: null,
      teamNameError: null,
      creatingTeam: false,
      invitingMembers: false,
      dontShowAgain: localStorage.getItem(`dont-show-team-invite-modal-again-${this.presentation.id}`),
    }
  },

  computed: {
    hasTeam() {
      return this.team && this.team.id
    },
    modalTitle() {
      if (this.hasTeam) {
        return this.$i18nPresenter.t('Invite collaborators to team\'s workspace')
      }
      return this.$i18nPresenter.t('Create a team with collaborators')
    },
    memberListText() {
      if (!this.toBeInvitedMembers || this.toBeInvitedMembers.length === 0) {
        return ''
      }
      const memberNames = this.toBeInvitedMembers.map((i) => {
        if (i.id && i.firstName) {
          return `${i.firstName} ${i.lastName}`.trim()
        }
        if (i.email) {
          return EmailUtils.getEmailName(i.email)
        }
        return this.$i18nPresenter.t('Unknown user')
      })
      if (this.toBeInvitedMembers.length < 4) {
        return memberNames.join(', ')
      }
      const numberOfOthers = this.toBeInvitedMembers.length - 3
      const numberOfOthersText = numberOfOthers === 1 ? '1 other' : `${numberOfOthers} others`
      return `${memberNames.slice(0, 3).join(', ')} and ${numberOfOthersText}`
    },
  },

  mounted() {
    util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Forming Team modal > Open modal', this.toBeInvitedMembers.map((i) => i.email).join(', '), null, {
      plan: this.user.plan,
      presentationId: this.presentation.id,
    })
  },

  methods: {
    ...mapActions([
      'getCurrentUserAction',
      'setCurrentAccountAction',
    ]),
    closeModal() {
      this.$emit('hide')
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Forming Team modal > Close modal')
    },
    onInputTeamName(teamName) {
      if (teamName && teamName.trim() !== '') {
        this.teamNameError = null
      }
    },
    getOwner(presentation) {
      return {
        ...presentation.User,
        role: 'owner',
      }
    },

    async createNewTeam() {
      if (!this.teamName || this.teamName.trim() === '') {
        this.teamNameError = this.$i18nPresenter.t('Please enter a name for team.')
        return
      }
      const createTeamResult = await AccountApi.createAccount({ name: this.teamName.trim() })
      this.createTeamResult = createTeamResult
    },

    async inviteMembersToTeam() {
      await InvitationApi.createInvitations({
        emails: this.toBeInvitedMembers.map((i) => i.email).filter((email) => email !== this.user.email),
        accountRole: 'editor',
        bypassRecaptcha: true, // We dont require our users to do recaptcha when inviting to team
      })

      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Invite to Edit > Forming Team modal > Invite members success', this.teamName, null, {
        plan: this.user.plan,
        presentationId: this.presentation.id,
        alreadyHasTeam: !!this.hasTeam,
      })
    },
    async createTeamAndInvitePeople() {
      this.creatingTeam = true
      let accumulatedError = ''
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Invite to Edit > Forming Team modal > Create', null, null, {
        plan: this.user.plan,
        presentationId: this.presentation.id,
      })
      try {
        await this.createNewTeam()
      } catch (e) {
        Sentry.captureException(e)
        accumulatedError = this.$i18nPresenter.t(e.body.message)
      }

      try {
        await this.inviteMembersToTeam()
        this.$emit('hide', {
          showSuccessModal: true,
          teamData: this.createTeamResult,
        })
        util.showToastSuccess(this.$i18nPresenter.t('Team created and invitations sent successfully!'))
      } catch (e) {
        Sentry.captureException(e)
        if (e.body && e.body.message) {
          if (accumulatedError) {
            accumulatedError += ` ${e.body.message}`
          } else {
            accumulatedError = this.$i18nPresenter.t(e.body.message)
          }
          util.showToastError(accumulatedError)
        }
      } finally {
        this.$emit('hide')
        this.creatingTeam = false
      }
    },

    async invitePeople() {
      this.invitingMembers = true
      try {
        await this.inviteMembersToTeam()
        util.showToastSuccess(this.$i18nPresenter.t('Invitations sent successfully!'))
      } catch (e) {
        Sentry.captureException(e)
        util.showToastError(this.$i18nPresenter.t(e.body.message))
      } finally {
        this.invitingMembers = false
        this.$emit('hide')
      }
    },
    hideInviteTeamModal() {
      this.closeModal()
    },
    onToggleShowAgain(isChecked) {
      localStorage.setItem(`dont-show-team-invite-modal-again-${this.presentation.id}`, JSON.stringify(isChecked))
      util.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Forming Team modal > Toggle Dont show again', isChecked, null, {
        plan: this.user.plan,
        presentationId: this.presentation.id,
        alreadyHasTeam: !!this.hasTeam,
      })
    },
  },
}
</script>

<style lang="scss" scoped>

</style>
